import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  FiArrowLeft,
  FiAtSign,
  FiBriefcase,
  FiCheck,
  FiCheckCircle,
  FiClock,
  FiDollarSign,
  FiMail,
  FiMapPin,
  FiMessageCircle,
  FiPhone,
  FiSend,
  FiShield,
  FiTool,
  FiUser,
  FiZap,
} from 'react-icons/fi';
import { FaInstagram, FaLinkedin, FaTelegram, FaTwitter, FaWhatsapp, FaYoutube } from 'react-icons/fa';
import ServiceOrderModal from '../components/ServiceOrderModal';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { useSiteSettings } from '../context/SiteSettingsContext';
import { api } from '../services/api';
import { hydrateServices } from '../utils/serviceContent';
import './Contact.css';

const TOPICS = [
  'website', 'ui-ux', 'plugin', 'branding', 'support', 'collaboration', 'other',
];

const BUDGETS = [
  '', 'under-20', '20-50', '50-100', 'over-100', 'unsure',
];

const initialForm = {
  name: '',
  email: '',
  phone: '',
  topic: 'website',
  budgetRange: '',
  preferredContact: 'email',
  message: '',
  privacyAccepted: false,
  website: '',
};

const normalizeDigits = (value) => String(value || '')
  .replace(/[۰-۹]/g, (digit) => String('۰۱۲۳۴۵۶۷۸۹'.indexOf(digit)))
  .replace(/[٠-٩]/g, (digit) => String('٠١٢٣٤٥٦٧٨٩'.indexOf(digit)));
const toTelHref = (value) => normalizeDigits(value).replace(/[^+\d]/g, '');

const resolveEmergencySupport = (services) => {
  const service = hydrateServices(services).find((item) => item.slug === 'website-support');
  const group = service?.planGroups?.find((item) => item.id === 'one-time-support' && item.mode === 'request');

  if (!service?._id || !group) {
    throw new Error('EMERGENCY_SUPPORT_UNAVAILABLE');
  }

  return { service, group };
};

const OfficeStatus = ({ workingHours, intlLocale }) => {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  const status = useMemo(() => {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'Asia/Tehran',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(now).reduce((result, part) => ({ ...result, [part.type]: part.value }), {});
    const minutes = (Number(parts.hour) * 60) + Number(parts.minute);
    const isOpenDay = parts.weekday !== 'Fri';
    return { isOpen: isOpenDay && minutes >= 540 && minutes < 1020 };
  }, [now]);

  const timeLabel = new Intl.DateTimeFormat(intlLocale, {
    timeZone: 'Asia/Tehran',
    weekday: 'long',
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);

  return (
    <div className={`contact-v2-office ${status.isOpen ? 'is-open' : 'is-closed'}`}>
      <span className="contact-v2-office-icon"><FiClock aria-hidden="true" /></span>
      <div>
        <span className="contact-v2-office-state"><i aria-hidden="true" /> {t(status.isOpen ? 'contactPage.officeOpen' : 'contactPage.officeClosed')}</span>
        <strong>{timeLabel}</strong>
        <small>{workingHours}</small>
      </div>
    </div>
  );
};

const Contact = () => {
  const { t } = useTranslation();
  const { locale, formatNumber } = useLocale();
  const { settings } = useSiteSettings();
  const { user } = useAuth();
  const { contact } = settings;
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [submitState, setSubmitState] = useState({ status: 'idle', message: '', ticketNumber: '' });
  const [supportEntry, setSupportEntry] = useState({ status: 'loading', service: null, group: null });
  const [supportLoadAttempt, setSupportLoadAttempt] = useState(0);
  const [supportOpenRequested, setSupportOpenRequested] = useState(false);
  const [supportModalOpen, setSupportModalOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    setForm((current) => ({
      ...current,
      name: current.name || user.name || '',
      email: current.email || user.email || '',
      phone: current.phone || user.phone || '',
    }));
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    setSupportEntry((current) => ({ ...current, status: 'loading' }));

    api.get(`/api/services?locale=${encodeURIComponent(locale.code)}`)
      .then((response) => {
        if (cancelled) return;
        const selection = resolveEmergencySupport(response.data);
        setSupportEntry({ status: 'ready', ...selection });
      })
      .catch(() => {
        if (!cancelled) setSupportEntry({ status: 'error', service: null, group: null });
      });

    return () => { cancelled = true; };
  }, [locale.code, supportLoadAttempt]);

  useEffect(() => {
    try {
      const pending = JSON.parse(sessionStorage.getItem('pendingServicePlan') || 'null');
      if (pending?.groupId === 'one-time-support' && pending.selectionType === 'quote_request') {
        setSupportOpenRequested(true);
      }
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (supportOpenRequested && supportEntry.status === 'ready') {
      setSupportModalOpen(true);
      setSupportOpenRequested(false);
    }
  }, [supportEntry.status, supportOpenRequested]);

  const socialLinks = [
    { key: 'instagram', label: t('contactPage.social.instagram'), url: contact.social.instagram, icon: FaInstagram },
    { key: 'telegram', label: t('contactPage.social.telegram'), url: contact.social.telegram, icon: FaTelegram },
    { key: 'linkedin', label: t('contactPage.social.linkedin'), url: contact.social.linkedin, icon: FaLinkedin },
    { key: 'whatsapp', label: t('contactPage.social.whatsapp'), url: contact.social.whatsapp, icon: FaWhatsapp },
    { key: 'x', label: t('contactPage.social.x'), url: contact.social.x, icon: FaTwitter },
    { key: 'youtube', label: t('contactPage.social.youtube'), url: contact.social.youtube, icon: FaYoutube },
  ].filter((item) => Boolean(item.url));

  const primaryPhone = contact.phone || contact.mobile;

  const updateField = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
    setErrors((current) => ({ ...current, [name]: '' }));
    if (submitState.status === 'error') setSubmitState({ status: 'idle', message: '', ticketNumber: '' });
  };

  const validate = () => {
    const nextErrors = {};
    if (form.name.trim().length < 2) nextErrors.name = t('contactPage.errors.name');
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) nextErrors.email = t('contactPage.errors.email');
    if (form.phone && !/^[+\d\s()-]{7,30}$/.test(normalizeDigits(form.phone).trim())) nextErrors.phone = t('contactPage.errors.phone');
    if (form.preferredContact !== 'email' && !form.phone.trim()) nextErrors.phone = t('contactPage.errors.phoneRequired');
    if (form.message.trim().length < 20) nextErrors.message = t('contactPage.errors.message');
    if (!form.privacyAccepted) nextErrors.privacyAccepted = t('contactPage.errors.privacy');
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validate()) return;
    setSubmitState({ status: 'loading', message: '', ticketNumber: '' });
    try {
      const response = await api.post('/api/contact-messages', {
        name: form.name,
        email: form.email,
        phone: form.phone,
        topic: form.topic,
        budgetRange: form.budgetRange,
        preferredContact: form.preferredContact,
        message: form.message,
        website: form.website,
        locale: locale.code,
      });
      setSubmitState({
        status: 'success',
        message: t('contactPage.successMessage'),
        ticketNumber: response.data?.ticketNumber || '',
      });
    } catch (error) {
      setSubmitState({ status: 'error', message: t('contactPage.submitError'), ticketNumber: '' });
    }
  };

  const resetForm = () => {
    setForm({ ...initialForm, name: user?.name || '', email: user?.email || '', phone: user?.phone || '' });
    setErrors({});
    setSubmitState({ status: 'idle', message: '', ticketNumber: '' });
  };

  const openEmergencySupport = () => {
    if (supportEntry.status === 'ready') {
      setSupportModalOpen(true);
      return;
    }

    setSupportOpenRequested(true);
    if (supportEntry.status === 'error') {
      setSupportLoadAttempt((current) => current + 1);
    }
  };

  const closeEmergencySupport = () => {
    sessionStorage.removeItem('pendingServicePlan');
    setSupportModalOpen(false);
    setSupportOpenRequested(false);
  };

  const supportActionLabel = supportEntry.status === 'error'
    ? t('contactPage.supportRetry')
    : supportOpenRequested
      ? t('contactPage.supportLoading')
      : t('contactPage.supportSubmit');

  return (
    <main className="contact-v2">
      <section className="contact-v2-hero" aria-labelledby="contact-page-title">
        <div className="contact-v2-shell contact-v2-hero-inner">
          <div>
            <span className="contact-v2-eyebrow"><FiMessageCircle aria-hidden="true" /> {t('contactPage.eyebrow')}</span>
            <h1 id="contact-page-title">{t('contactPage.title')}</h1>
            <p>{t('contactPage.intro')}</p>
          </div>
          <div className="contact-v2-hero-promise">
            <FiShield aria-hidden="true" />
            <div><strong>{t('contactPage.secure')}</strong><span>{t('contactPage.secureHint')}</span></div>
          </div>
        </div>
      </section>

      <section className="contact-v2-main">
        <div className="contact-v2-shell contact-v2-grid">
          <section className="contact-v2-form-card" aria-labelledby="contact-form-title">
            {submitState.status === 'success' ? (
              <div className="contact-v2-success" role="status">
                <span><FiCheckCircle aria-hidden="true" /></span>
                <small>{t('contactPage.successKicker')}</small>
                <h2>{t('contactPage.successTitle')}</h2>
                <p>{submitState.message} {t('contactPage.successFollowup')}</p>
                {submitState.ticketNumber && (
                  <div className="contact-v2-ticket"><span>{t('contactPage.ticket')}</span><strong dir="ltr">{submitState.ticketNumber}</strong></div>
                )}
                <button type="button" onClick={resetForm}>{t('contactPage.newMessage')}</button>
              </div>
            ) : (
              <>
                <header className="contact-v2-form-heading">
                  <span className="contact-v2-step">{t('contactPage.form')}</span>
                  <h2 id="contact-form-title">{t('contactPage.formTitle')}</h2>
                  <p>{t('contactPage.formHint')}</p>
                </header>

                <form className="contact-v2-form" onSubmit={handleSubmit} noValidate>
                  <div className="contact-v2-field-grid">
                    <label className={`contact-v2-field ${errors.name ? 'has-error' : ''}`}>
                      <span>{t('contactPage.name')} <b>*</b></span>
                      <div><FiUser aria-hidden="true" /><input name="name" value={form.name} onChange={updateField} autoComplete="name" placeholder={t('contactPage.namePlaceholder')} aria-invalid={Boolean(errors.name)} /></div>
                      {errors.name && <small>{errors.name}</small>}
                    </label>
                    <label className={`contact-v2-field ${errors.email ? 'has-error' : ''}`}>
                      <span>{t('contactPage.email')} <b>*</b></span>
                      <div><FiAtSign aria-hidden="true" /><input type="email" name="email" value={form.email} onChange={updateField} autoComplete="email" placeholder="name@example.com" dir="ltr" aria-invalid={Boolean(errors.email)} /></div>
                      {errors.email && <small>{errors.email}</small>}
                    </label>
                    <label className={`contact-v2-field ${errors.phone ? 'has-error' : ''}`}>
                      <span>{t('contactPage.phone')} <i>{t('contactPage.optional')}</i></span>
                      <div><FiPhone aria-hidden="true" /><input type="tel" name="phone" value={form.phone} onChange={updateField} autoComplete="tel" placeholder="09xxxxxxxxx" dir="ltr" aria-invalid={Boolean(errors.phone)} /></div>
                      {errors.phone && <small>{errors.phone}</small>}
                    </label>
                    <label className="contact-v2-field">
                      <span>{t('contactPage.topic')} <b>*</b></span>
                      <div><FiBriefcase aria-hidden="true" /><select name="topic" value={form.topic} onChange={updateField}>{TOPICS.map((item) => <option key={item} value={item}>{t(`contactPage.topics.${item.replace('-', '_')}`)}</option>)}</select></div>
                    </label>
                    <label className="contact-v2-field contact-v2-field-full">
                      <span>{t('contactPage.budget')} <i>{t('contactPage.optional')}</i></span>
                      <div><FiDollarSign aria-hidden="true" /><select name="budgetRange" value={form.budgetRange} onChange={updateField}>{BUDGETS.map((item) => <option key={item || 'unknown'} value={item}>{t(`contactPage.budgets.${(item || 'unknown').replaceAll('-', '_')}`)}</option>)}</select></div>
                    </label>
                  </div>

                  <fieldset className="contact-v2-contact-method">
                    <legend>{t('contactPage.preferredContact')}</legend>
                    <div>
                      {[
                        { value: 'email', icon: FiMail },
                        { value: 'phone', icon: FiPhone },
                        { value: 'whatsapp', icon: FaWhatsapp },
                      ].map(({ value, icon: Icon }) => (
                        <label key={value} className={form.preferredContact === value ? 'is-selected' : ''}>
                          <input type="radio" name="preferredContact" value={value} checked={form.preferredContact === value} onChange={updateField} />
                          <Icon aria-hidden="true" /><span>{t(`contactPage.contactMethods.${value}`)}</span><FiCheck className="contact-v2-method-check" aria-hidden="true" />
                        </label>
                      ))}
                    </div>
                  </fieldset>

                  <label className={`contact-v2-field contact-v2-message-field ${errors.message ? 'has-error' : ''}`}>
                    <span>{t('contactPage.message')} <b>*</b><i>{formatNumber(form.message.length)} / {formatNumber(3000)}</i></span>
                    <textarea name="message" value={form.message} onChange={updateField} rows="7" maxLength="3000" placeholder={t('contactPage.messagePlaceholder')} aria-invalid={Boolean(errors.message)} />
                    {errors.message && <small>{errors.message}</small>}
                  </label>

                  <label className={`contact-v2-privacy ${errors.privacyAccepted ? 'has-error' : ''}`}>
                    <input type="checkbox" name="privacyAccepted" checked={form.privacyAccepted} onChange={updateField} />
                    <span>{t('contactPage.privacy')}</span>
                  </label>
                  {errors.privacyAccepted && <small className="contact-v2-privacy-error">{errors.privacyAccepted}</small>}

                  <label className="contact-v2-honeypot" aria-hidden="true">
                    {t('contactPage.website')}<input name="website" value={form.website} onChange={updateField} tabIndex="-1" autoComplete="off" />
                  </label>

                  {submitState.status === 'error' && <div className="contact-v2-submit-error" role="alert">{submitState.message}</div>}
                  <button className="contact-v2-submit" type="submit" disabled={submitState.status === 'loading'}>
                    {submitState.status === 'loading' ? t('contactPage.sending') : <>{t('contactPage.send')} <FiSend aria-hidden="true" /></>}
                  </button>
                </form>
              </>
            )}
          </section>

          <aside className="contact-v2-sidebar" aria-label={t('contactPage.contactWays')}>
            <OfficeStatus workingHours={contact.workingHours} intlLocale={locale.intlLocale || locale.code} />

            <div className="contact-v2-direct-card">
              <header><span>{t('contactPage.direct')}</span><h2>{t('contactPage.directTitle')}</h2></header>
              <div className="contact-v2-direct-list">
                {primaryPhone && (
                  <a href={`tel:${toTelHref(primaryPhone)}`}>
                    <span><FiPhone aria-hidden="true" /></span><div><small>{t('contactPage.phoneCall')}</small><strong dir="ltr">{primaryPhone}</strong></div><FiArrowLeft aria-hidden="true" />
                  </a>
                )}
                {contact.supportEmail && (
                  <a href={`mailto:${contact.supportEmail}`}>
                    <span><FiMail aria-hidden="true" /></span><div><small>{t('contactPage.supportEmail')}</small><strong dir="ltr">{contact.supportEmail}</strong></div><FiArrowLeft aria-hidden="true" />
                  </a>
                )}
                {contact.address && (
                  <div className="contact-v2-address-row">
                    <span><FiMapPin aria-hidden="true" /></span><div><small>{t('contactPage.address')}</small><strong>{contact.address}</strong></div>
                  </div>
                )}
              </div>
            </div>

            <div className="contact-v2-prepare-card">
              <h3>{t('contactPage.prepareTitle')}</h3>
              <p>{t('contactPage.prepareText')}</p>
              <ul>
                <li><FiCheckCircle aria-hidden="true" /> {t('contactPage.prepareGoal')}</li>
                <li><FiCheckCircle aria-hidden="true" /> {t('contactPage.prepareScope')}</li>
                <li><FiCheckCircle aria-hidden="true" /> {t('contactPage.prepareTimeline')}</li>
              </ul>
            </div>

            {socialLinks.length > 0 && (
              <div className="contact-v2-social-card">
                <span>{t('contactPage.onSocial')}</span>
                <div>{socialLinks.map(({ key, label, url, icon: Icon }) => <a key={key} href={url} target="_blank" rel="noopener noreferrer" aria-label={label}><Icon aria-hidden="true" /></a>)}</div>
              </div>
            )}

            <button
              type="button"
              className="contact-v2-emergency-card"
              onClick={openEmergencySupport}
              aria-haspopup="dialog"
              aria-busy={supportOpenRequested && supportEntry.status === 'loading'}
            >
              <span className="contact-v2-emergency-visual" aria-hidden="true">
                <FiTool />
                <i><FiZap /></i>
              </span>
              <span className="contact-v2-emergency-copy">
                <span className="contact-v2-emergency-kicker"><i aria-hidden="true" /> {t('contactPage.quickSupport')}</span>
                <strong>{t('contactPage.emergencyTitle')}</strong>
                <small>{t('contactPage.emergencyText')}</small>
              </span>
              <span className="contact-v2-emergency-meta">
                <span><FiShield aria-hidden="true" /> {t('contactPage.noPayment')}</span>
                <span><FiClock aria-hidden="true" /> {t('contactPage.responseTime')}</span>
              </span>
              <span className="contact-v2-emergency-action">
                {supportActionLabel}
                <FiArrowLeft aria-hidden="true" />
              </span>
            </button>
          </aside>
        </div>
      </section>

      {contact.mapEmbedUrl && (
        <section className="contact-v2-location" aria-labelledby="contact-location-title">
          <div className="contact-v2-shell">
            <header>
              <div><span className="contact-v2-eyebrow"><FiMapPin aria-hidden="true" /> {t('contactPage.location')}</span><h2 id="contact-location-title">{t('contactPage.locationTitle')}</h2></div>
              <p>{contact.address}</p>
            </header>
            <div className="contact-v2-map-frame">
              <iframe src={contact.mapEmbedUrl} title={t('contactPage.mapTitle')} loading="lazy" allowFullScreen referrerPolicy="no-referrer-when-downgrade" />
            </div>
          </div>
        </section>
      )}

      {supportModalOpen && supportEntry.service && supportEntry.group && (
        <ServiceOrderModal
          service={supportEntry.service}
          group={supportEntry.group}
          plan={null}
          onClose={closeEmergencySupport}
        />
      )}
    </main>
  );
};

export default Contact;
